import { match } from "ts-pattern";
import { HeartHandshake as HeartHandshakeIcon, MessagesSquare as MessagesSquareIcon, ShieldCheck as ShieldCheckIcon } from "@tamagui/lucide-icons";
import { Badge, Body1, Card, type IconComponent, InfoRow, ResponsiveImage, TextSpan, Title4, Title5, View, XStack, YStack } from "@medbillai/ui";
import { OnboardingScreen } from "../../components/OnboardingScreen";
import { type OnDone, tagged } from "../../lib/flow-engine";
import type { Intent } from "./types";
export type EducationOutcomes = {
  continue: {};
  back: {};
};
const Outcome = tagged<EducationOutcomes>();
const HEADER_COPY: Record<Intent, {
  title: string;
  subtitle?: string;
}> = {
  bill: {
    title: "Feeling overcharged? You're not alone"
  },
  learn: {
    title: "Find out what your plan covers",
    subtitle: "Ask anything about your benefits in plain English. No jargon, no hold music."
  },
  other: {
    title: "We're building a healthcare advocate at the speed of AI"
  }
};
export function Education({
  intent,
  onDone
}: {
  intent: Intent;
  onDone: OnDone<EducationOutcomes>;
}) {
  const {
    title,
    subtitle
  } = HEADER_COPY[intent];
  return <OnboardingScreen data-sentry-element="OnboardingScreen" data-sentry-component="Education" data-sentry-source-file="Education.tsx">
      <OnboardingScreen.Nav onBackPress={() => onDone(Outcome.back())} showLogo={false} data-sentry-element="OnboardingScreen.Nav" data-sentry-source-file="Education.tsx" />
      <OnboardingScreen.Content data-sentry-element="OnboardingScreen.Content" data-sentry-source-file="Education.tsx">
        <OnboardingScreen.Header alignment="left" {...intent === "other" ? {
        badge: "Available now"
      } : {}} title={title} subtitle={subtitle} data-sentry-element="OnboardingScreen.Header" data-sentry-source-file="Education.tsx" />
        {match(intent).with("bill", () => <BillBody />).with("learn", () => <LearnBody />).with("other", () => <OtherBody />).exhaustive()}
      </OnboardingScreen.Content>
      <OnboardingScreen.Footer data-sentry-element="OnboardingScreen.Footer" data-sentry-source-file="Education.tsx">
        <OnboardingScreen.PrimaryAction testID="education.continue" variant="brand" onPress={() => onDone(Outcome.continue())} data-sentry-element="OnboardingScreen.PrimaryAction" data-sentry-source-file="Education.tsx">
          Continue
        </OnboardingScreen.PrimaryAction>
      </OnboardingScreen.Footer>
    </OnboardingScreen>;
}
function BillBody() {
  return <YStack testID="education.bill-body" gap="$sp2" data-sentry-element="YStack" data-sentry-component="BillBody" data-sentry-source-file="Education.tsx">
      <XStack gap="$sp1.5" data-sentry-element="XStack" data-sentry-source-file="Education.tsx">
        <StatCard headline="8 in 10" body="medical bills contain errors" data-sentry-element="StatCard" data-sentry-source-file="Education.tsx" />
        <StatCard headline="93%" body="of patients never dispute a charge" data-sentry-element="StatCard" data-sentry-source-file="Education.tsx" />
      </XStack>
      <Card variant="elevated" size="large" data-sentry-element="Card" data-sentry-source-file="Education.tsx">
        <YStack gap="$sp1.5" data-sentry-element="YStack" data-sentry-source-file="Education.tsx">
          <XStack justifyContent="space-between" data-sentry-element="XStack" data-sentry-source-file="Education.tsx">
            <YStack alignItems="flex-start" justifyContent="space-between" gap="$sp1" data-sentry-element="YStack" data-sentry-source-file="Education.tsx">
              <Badge status="positive" data-sentry-element="Badge" data-sentry-source-file="Education.tsx">The Granted Difference</Badge>
              <Title5 data-sentry-element="Title5" data-sentry-source-file="Education.tsx">Save 70% on average</Title5>
            </YStack>
            <View alignSelf="flex-end" data-sentry-element="View" data-sentry-source-file="Education.tsx">
              <ResponsiveImage alt="Analyze my medical bill" src={{
              web: "/static/onboarding/intent-bill.png",
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-require-imports
              native: require("../../assets/onboarding/intent-bill.png")
            }} objectFit="contain" height={48} width={48} data-sentry-element="ResponsiveImage" data-sentry-source-file="Education.tsx" />
            </View>
          </XStack>
          <Body1 color="$textSubtle" data-sentry-element="Body1" data-sentry-source-file="Education.tsx">
            We review every line of your bill, catch mistakes and negotiate it
            down{" "}
            <TextSpan bold unstyled data-sentry-element="TextSpan" data-sentry-source-file="Education.tsx">
              for free
            </TextSpan>
            .
          </Body1>
        </YStack>
      </Card>
    </YStack>;
}
function LearnBody() {
  return <YStack testID="education.learn-body" flex={1} minHeight={0} width="100%" height="100%" alignItems="center" data-sentry-element="YStack" data-sentry-component="LearnBody" data-sentry-source-file="Education.tsx">
      <ResponsiveImage alt="Learn about your benefits" src={{
      web: "/static/onboarding/education-intent-learn.png",
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-require-imports
      native: require("../../assets/onboarding/education-intent-learn.png")
    }} objectFit="contain" height="100%" minHeight={200} maxWidth="100%"
    // biome-ignore lint/plugin/no-fixed-style-props: migrated from eslint-disable
    width="auto" data-sentry-element="ResponsiveImage" data-sentry-source-file="Education.tsx" />
    </YStack>;
}
function OtherBody() {
  const items: ReadonlyArray<{
    Icon: IconComponent;
    title: string;
    body: string;
  }> = [{
    Icon: ShieldCheckIcon,
    title: "Review your claims",
    body: "Connect your insurance and we will review your claims for savings"
  }, {
    Icon: MessagesSquareIcon,
    title: "Ask us anything",
    body: "Billing help, coverage confusion and finding care — we can help"
  }, {
    Icon: HeartHandshakeIcon,
    title: "Talk to an expert",
    body: "Choose between AI assistance or 1:1 help with real health experts"
  }];
  return <YStack testID="education.other-body" gap="$sp3" data-sentry-element="YStack" data-sentry-component="OtherBody" data-sentry-source-file="Education.tsx">
      {items.map(({
      Icon,
      title,
      body
    }) => <InfoRow key={title} icon={Icon} title={title} description={body} />)}
    </YStack>;
}
function StatCard({
  headline,
  body
}: {
  headline: string;
  body: string;
}) {
  return <Card flex={1} backgroundColor="$surface0" borderWidth={0} data-sentry-element="Card" data-sentry-component="StatCard" data-sentry-source-file="Education.tsx">
      <YStack gap="$sp1" data-sentry-element="YStack" data-sentry-source-file="Education.tsx">
        <Title4 data-sentry-element="Title4" data-sentry-source-file="Education.tsx">{headline}</Title4>
        <Body1 color="$textSubtle" data-sentry-element="Body1" data-sentry-source-file="Education.tsx">{body}</Body1>
      </YStack>
    </Card>;
}